import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { circlePolygon } from "./geo_circle"

// Interactive center+radius picker for area (outfitter) listings. Shows only
// when listing_type = "area". Drag the center marker (or click the map) to set
// the service-area center — it writes the shared lat/lng inputs — and the radius
// input redraws the advertised circle. Reads/writes the lat/lng inputs by id so
// it stays in sync with the address geocoder above it. Defers building the map
// until its container is actually visible (it lives on a hidden wizard step).
export default class extends Controller {
  static targets = ["mapContainer", "areaSection", "listingType", "radius"]
  static values = {
    defaultLat: { type: Number, default: 31.2 },  // ~center of Texas
    defaultLng: { type: Number, default: -99.0 },
  }

  connect() {
    this.form = this.element.closest("form")
    this.latInput = this.form?.querySelector("#property_latitude")
    this.lngInput = this.form?.querySelector("#property_longitude")
    this.token = document.querySelector('meta[name="mapbox-token"]')?.content

    // The geocoder (or a manual edit) changes the coordinates — recenter.
    this._onCoordChange = () => this.#syncFromInputs()
    this.latInput?.addEventListener("input", this._onCoordChange)
    this.lngInput?.addEventListener("input", this._onCoordChange)

    this.refresh()
  }

  disconnect() {
    this.latInput?.removeEventListener("input", this._onCoordChange)
    this.lngInput?.removeEventListener("input", this._onCoordChange)
    this._io?.disconnect()
    this.map?.remove()
  }

  // Toggle the area section and (lazily) build the map when "area" is chosen.
  refresh() {
    const isArea = this.hasListingTypeTarget && this.listingTypeTarget.value === "area"
    if (this.hasAreaSectionTarget) this.areaSectionTarget.classList.toggle("d-none", !isArea)
    if (isArea) this.#observeVisibility()
  }

  updateRadius() {
    this.#drawCircle()
    this.#fitToCircle()
  }

  // ── private ───────────────────────────────────────────────────────────────

  // Build the map only once its container is on-screen (it sits on a hidden
  // wizard step), and resize it whenever it re-appears.
  #observeVisibility() {
    if (!this.hasMapContainerTarget || !this.token) return
    if (this._io) { this.map?.resize(); return }

    this._io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      if (!this.map) this.#initMap()
      else this.map.resize()
    })
    this._io.observe(this.mapContainerTarget)
  }

  #center() {
    const lat = parseFloat(this.latInput?.value) || this.defaultLatValue
    const lng = parseFloat(this.lngInput?.value) || this.defaultLngValue
    return [lng, lat]
  }

  #radiusMiles() {
    const v = parseFloat(this.hasRadiusTarget ? this.radiusTarget.value : 0)
    return Number.isFinite(v) && v > 0 ? v : 0
  }

  #initMap() {
    mapboxgl.accessToken = this.token
    const center = this.#center()

    this.map = new mapboxgl.Map({
      container: this.mapContainerTarget,
      style: "mapbox://styles/mapbox/dark-v11",
      center,
      zoom: 8,
    })
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this.marker = new mapboxgl.Marker({ color: "#48BA16", draggable: true })
      .setLngLat(center)
      .addTo(this.map)
    this.marker.on("dragend", () => {
      const { lng, lat } = this.marker.getLngLat()
      this.#writeInputs(lng, lat)
      this.#drawCircle()
    })

    this.map.on("click", (e) => {
      this.marker.setLngLat(e.lngLat)
      this.#writeInputs(e.lngLat.lng, e.lngLat.lat)
      this.#drawCircle()
    })

    this.map.on("load", () => {
      this.map.addSource("area-circle", { type: "geojson", data: this.#circleData() })
      this.map.addLayer({
        id: "area-circle-fill", type: "fill", source: "area-circle",
        paint: { "fill-color": "#48BA16", "fill-opacity": 0.15 },
      })
      this.map.addLayer({
        id: "area-circle-line", type: "line", source: "area-circle",
        paint: { "line-color": "#48BA16", "line-width": 2 },
      })
      this.#fitToCircle()
    })
  }

  #circleData() {
    const [lng, lat] = this.#center()
    const r = this.#radiusMiles()
    return r > 0 ? circlePolygon(lng, lat, r) : { type: "FeatureCollection", features: [] }
  }

  #drawCircle() {
    this.map?.getSource("area-circle")?.setData(this.#circleData())
  }

  #fitToCircle() {
    const r = this.#radiusMiles()
    if (!this.map || r <= 0) return
    const [lng, lat] = this.#center()
    const dLat = r / 69
    const dLng = r / (69 * Math.cos((lat * Math.PI) / 180))
    this.map.fitBounds([[lng - dLng, lat - dLat], [lng + dLng, lat + dLat]], { padding: 30, duration: 0 })
  }

  #writeInputs(lng, lat) {
    if (this.latInput) this.latInput.value = lat.toFixed(6)
    if (this.lngInput) this.lngInput.value = lng.toFixed(6)
  }

  #syncFromInputs() {
    if (!this.map || !this.marker) return
    const center = this.#center()
    this.marker.setLngLat(center)
    this.map.setCenter(center)
    this.#drawCircle()
    this.#fitToCircle()
  }
}
